/**
 * Job Description Formatting Utility
 * Premium, clean formatting for job descriptions
 */

/**
 * Format a job description from plain text to clean, premium HTML
 * @param {string} desc - The raw job description text
 * @returns {string} - Formatted HTML string
 */
export const formatJobDescription = (desc) => {
  if (!desc) return '<p class="text-zinc-500 italic">No description available</p>';
  
  // If already HTML formatted, return as is
  if (desc.includes('<h3>') || desc.includes('<h4>') || (desc.includes('<ul>') && desc.includes('<li>'))) {
    return desc;
  }
  
  // Section headers to detect (order matters - longer phrases first)
  const sectionHeaders = [
    'About the Opportunity',
    'About The Opportunity',
    'Job Responsibilities',
    'Key Responsibilities',
    'Core Responsibilities',
    'Role Overview',
    'Role overview',
    'About Role',
    'About the Role',
    'About The Role',
    'Responsibilities',
    'Requirements',
    'Qualifications',
    'What You\'ll Do',
    'What You\u2019ll Do',
    'What You Will Do',
    'What You Bring',
    'What You\'ll Bring',
    'What You\u2019ll Bring',
    'What We Offer',
    'Benefits',
    'About Us',
    'Duties',
    'Experience',
    'Skills',
    'Working Conditions',
    'Please Note',
    'How to Apply'
  ];
  
  // Clean up the text
  let text = desc
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n');
  
  // Pre-process: Add newlines before section headers that appear inline
  sectionHeaders.forEach(header => {
    const escapedHeader = header.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex1 = new RegExp(`([.!?])\\s*(${escapedHeader})\\s+([A-Z])`, 'gi');
    text = text.replace(regex1, '$1\n\n$2\n\n$3');
    const regex2 = new RegExp(`^(${escapedHeader})\\s+([A-Z])`, 'gi');
    text = text.replace(regex2, '$1\n\n$2');
  });
  
  const lines = text.split('\n');
  let html = [];
  let currentBullets = [];
  let isFirstSection = true;
  
  const flushBullets = () => {
    if (currentBullets.length > 0) {
      html.push('<ul class="space-y-3 mb-6">');
      currentBullets.forEach(bullet => {
        html.push(`<li class="flex items-start gap-3">
          <span class="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2.5 flex-shrink-0"></span>
          <span class="text-zinc-700 leading-relaxed">${bullet}</span>
        </li>`);
      });
      html.push('</ul>');
      currentBullets = [];
    }
  };
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    
    const matchedHeader = sectionHeaders.find(header => {
      const lineLower = line.toLowerCase().replace(/:$/, '');
      const headerLower = header.toLowerCase();
      return lineLower === headerLower;
    });
    
    if (matchedHeader) {
      flushBullets();
      const headerText = line.replace(/:$/, '');
      const marginTop = isFirstSection ? 'mt-0' : 'mt-8';
      html.push(`<h3 class="text-lg font-semibold text-zinc-900 ${marginTop} mb-4">${headerText}</h3>`);
      isFirstSection = false;
      continue;
    }
    
    if (line.match(/^[\*·\-•]\s+/) || line.match(/^\*\s+/)) {
      const bulletText = line.replace(/^[\*·\-•]\s+/, '').trim();
      if (bulletText) currentBullets.push(bulletText);
      continue;
    }
    
    if (line.match(/^\s+[\*·\-•]\s+/)) {
      const bulletText = line.replace(/^\s+[\*·\-•]\s+/, '').trim();
      if (bulletText) currentBullets.push(bulletText);
      continue;
    }
    
    flushBullets();
    html.push(`<p class="text-zinc-700 leading-relaxed mb-4">${line}</p>`);
  }
  
  flushBullets();
  return html.join('\n') || `<p class="text-zinc-700">${desc}</p>`;
};

/**
 * Format Zoho job description with advanced parsing
 * Handles inline bullets, sub-labels, and complex formatting
 * @param {string} desc - The raw job description text
 * @param {string} jobTitle - The job title (to remove from description start)
 * @returns {string} - Formatted HTML string
 */
export const formatZohoDescription = (desc, jobTitle = '') => {
  if (!desc) return '<p class="text-zinc-500 italic">No description available</p>';
  
  // If already HTML formatted from backend (has styled h3 or styled ul), return as is
  if (desc.includes('<h3') || desc.includes('<ul style=') || desc.includes('<ul class=')) {
    return desc;
  }
  
  let text = desc;
  
  // Remove job title and metadata from start
  if (jobTitle) {
    const titleEscaped = jobTitle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    text = text.replace(new RegExp(`^${titleEscaped}\\s*`, 'i'), '');
  }
  text = text.replace(/^[A-Za-z\s,]+(?:Company:\s*[A-Za-z\s]+)?(?:Employment Type:\s*[A-Za-z\-]+)?\s*(?=About)/i, '');
  
  // Main section headers
  const headers = [
    'About the Opportunity', 'About The Opportunity', 
    'Job Responsibilities', 'Key Responsibilities', 'Core Responsibilities',
    'About the Role', 'About The Role', 'Role Overview',
    'What You Will Do', "What You'll Do", "What You'll Bring", 'What You Bring',
    "What We're Looking For", 'What We Offer',
    'Responsibilities', 'Requirements', 'Qualifications',
    'Please Note', 'How to Apply', 'Why Join Us', 'Your Impact',
    'Our Values', 'What We Value', 'Who You Are', 'Ideal Candidate',
    'About Us', 'Benefits', 'Duties', 'The Role', 'About Role'
  ].sort((a, b) => b.length - a.length);
  
  // Sub-section labels
  const subLabels = [
    'Educational Background',
    'Industry Experience',
    'Technical and Process Skills',
    'Communication Skills',
    'Mobility',
    'Skills Required',
    'Compensation'
  ];
  
  // Mark main headers
  headers.forEach(h => {
    const escaped = h.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    text = text.replace(new RegExp(`(\\s|^)(${escaped})(\\s|:|$)`, 'gi'), '\n<<H>>$2<<H>>\n');
  });
  
  // Mark sub-labels as bullets
  subLabels.forEach(label => {
    const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    text = text.replace(new RegExp(`(\\s|^)(${escaped}:)`, 'gi'), '\n• **$2**');
  });
  
  // Split all inline bullets into separate lines
  text = text.replace(/\s*·\s*/g, '\n• ');
  text = text.replace(/\s*•\s*/g, '\n• ');
  
  const lines = text.split('\n').map(l => l.trim()).filter(l => l);
  
  let html = [];
  let bullets = [];
  let headerCount = 0;
  
  const flush = () => {
    if (bullets.length) {
      html.push('<ul class="space-y-3 my-4">');
      bullets.forEach(b => {
        if (!b || b.trim() === '' || b.trim() === '**') return;
        
        if (b.startsWith('**')) {
          const match = b.match(/^\*\*(.+?:)\*\*\s*(.*)/);
          if (match) {
            html.push(`<li class="flex items-start gap-3"><span class="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span><span class="text-zinc-700 leading-relaxed"><strong class="text-zinc-900">${match[1]}</strong> ${match[2]}</span></li>`);
            return;
          }
        }
        html.push(`<li class="flex items-start gap-3"><span class="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span><span class="text-zinc-700 leading-relaxed">${b}</span></li>`);
      });
      html.push('</ul>');
      bullets = [];
    }
  };
  
  for (const line of lines) {
    if (/Company:|Employment Type:/i.test(line)) continue;
    
    if (line.includes('<<H>>')) {
      flush();
      const h = line.replace(/<<H>>/g, '').trim();
      html.push(`<h3 class="text-lg font-bold text-zinc-900 ${headerCount ? 'mt-8' : ''} mb-3">${h}</h3>`);
      headerCount++;
      continue;
    }
    
    if (line.startsWith('•') || line.startsWith('·') || line.startsWith('-') || line.startsWith('*')) {
      bullets.push(line.replace(/^[•·\-\*]\s*/, ''));
      continue;
    }
    
    flush();
    html.push(`<p class="text-zinc-700 leading-relaxed mb-4">${line}</p>`);
  }
  
  flush();
  return html.join('\n');
};

export default formatJobDescription;
